// Brand + content configuration for Growth Therapy
import logo from "@/assets/logo.png";

export const BRAND = {
  name: "Growth Therapy",
  tagline: ["From Running Ads", "to Converting them."],
  logo,
  whatsappNumber: "917666666932",
  whatsappMessage: "Hi, would like to talk more regarding this.",
  instagram: "https://www.instagram.com/growththerapyhq",
  linkedin: "https://www.linkedin.com/company/growththerapyhq",
};

export const waLink = () =>
  `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(
    BRAND.whatsappMessage
  )}`;

// Screenshot/preview helper: ?static=1 disables the preloader + entrance
// animations so the fully-rendered layout can be captured. No effect for
// real visitors who load the site normally.
export const STATIC =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("static");

export const HERO = {
  badge: "Avoid the hassle of cold leads",
  subtext:
    "Beyond lead generation: we nurture and convert. High-intent prospects, zero fluff, maximum impact. See our recent work below.",
  button: "Book a call",
};

export const STATS = [
  { value: 3, suffix: "", label: "Key Locations" },
  { value: 2.4, prefix: "₹", suffix: " Cr+", decimals: 2, label: "Ad spend managed" },
  { value: 55, suffix: "+", label: "Brands scaled" },
  { value: 4.7, suffix: "x", decimals: 1, label: "Avg. revenue growth" },
];

export const CASE_STUDIES = [
  {
    id: "cs-01",
    index: "01",
    client: "Fertility Treatment",
    category: "Meta Ads | Podcasts | Social Proof",
    headline: "33.33X ROAS for a Fertility Treatment",
    result: "34x",
    metric: "Return on ad spend",
    body: "Aligned ad targeting across multiple centers across India to connect with high-intent patients who needed advanced fertility solutions.",
    tags: ["Creative Strategy", "Meta Ads", "Paid Social"],
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    span: "lg:col-span-7",
    objective:
      "To drive qualified patient enrollments for a specialized high-complexity fertility treatment plan tailored specifically for couples facing recurrent pregnancy loss and multiple IVF failures.",
    challenge:
      "Because the treatment protocol was relatively unknown in the market, cold audience skepticism was high. We needed to build deep trust, authority, and medical credibility from scratch — while navigating delicate patient emotions — rather than relying on standard, hard-sell lead generation tactics.",
    strategy: [
      {
        title: "Empathy-First Messaging",
        body: "Crafted sensitive, trust-led ad creative that validated patient struggles, respected emotional nuances, and highlighted clinical expertise.",
      },
      {
        title: "Automated & Empathetic Funnel",
        body: "Built a seamless, high-touch lead nurture workflow to handle inquiries instantly, guiding vulnerable prospects with prompt, personalized communication.",
      },
      {
        title: "Infrastructure Optimization",
        body: "Aligned ad targeting across multiple centers across India to connect with high-intent patients who needed advanced fertility solutions.",
      },
    ],
    results: [
      { value: "33X", label: "ROAS achieved within 60 days across multi-center launches in India" },
      { value: "1L++", label: "Monthly ad spend" },
      { value: "20%", label: "Interested rates" },
      { value: "90 days", label: "To hit profitable scale" },
    ],
  },
  {
    id: "cs-02",
    index: "02",
    client: "Dental Implants",
    category: "Meta Ads · GMB Optimisation",
    headline: "20X ROAS for Dental Implant Treatment in 90 Days",
    result: "₹47.38",
    metric: "Cost per lead",
    body: "Built a rapid-response lead funnel to instantly capture and nurture patients while interest peaked, with precision geotargeting inside a strict 5km radius of the clinic.",
    tags: ["Low CPL", "High Acquisition", "Meta Ads"],
    image:
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    span: "lg:col-span-5",
    objective:
      "To generate high-intent inquiries and consultation bookings for a premium dental implant offer priced aggressively at ₹20,000 (inclusive of a high-quality crown/cap) across Western Suburbs clinics.",
    challenge:
      "Despite offering an industry-leading price-to-quality ratio, the clinic lacked local brand awareness. The primary challenge was getting this compelling offer in front of nearby residents actively seeking dental procedures before competitors did.",
    strategy: [
      {
        title: "Laser-Focused Hyperlocal Targeting",
        body: "Deployed precision geotargeting within a strict 5km radius of the clinic to capture nearby high-intent patients.",
      },
      {
        title: "Transparent, Value-Driven Creative",
        body: "Focused on clear pricing transparency (₹20,000 with cap included) to pre-qualify prospects and eliminate low-intent inquiries.",
      },
      {
        title: "Streamlined Lead Capture",
        body: "Built a rapid-response lead funnel to instantly capture and nurture patients while interest was at its peak.",
      },
    ],
    results: [
      { value: "₹47.38", label: "Cost per lead" },
      { value: "20X", label: "ROAS delivered" },
      { value: "52k+", label: "Local residents reached" },
    ],
  },
  {
    id: "cs-03",
    index: "03",
    client: "Skin & Aesthetic Clinic",
    category: "Meta Ad · Offer Ad",
    headline: "238+ Conversations Started",
    result: "238+",
    metric: "Conversation enquiries started",
    body: "Seamlessly transitioned high-interest conversations directly into in-clinic consultation bookings before momentum cooled.",
    tags: ["Offer Ad", "Meta Ad", "New Innovation"],
    image:
      "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    span: "lg:col-span-5",
    objective:
      "To successfully launch a cutting-edge aesthetic innovation — Hair Fillers — into the local market by educating potential patients, building product awareness, and driving direct treatment consultations.",
    challenge:
      "As a brand-new technology in hair restoration, consumers had zero prior knowledge of hair fillers. The primary obstacle was bridging the awareness gap, addressing treatment skepticism, and answering clinical doubts to guide curious prospects from interest to active consultation.",
    strategy: [
      {
        title: "Education-First Content",
        body: "Developed targeted, highly engaging creative that clearly explained what hair fillers are, how they work, and who they are best suited for.",
      },
      {
        title: "Conversational Lead Funnel",
        body: "Designed a campaign flow focused on triggering two-way dialogue — allowing prospective patients to ask questions, clarify doubts, and receive instant, personalized advice.",
      },
      {
        title: "Rapid Conversion Pathway",
        body: "Seamlessly transitioned high-interest conversations directly into in-clinic consultation bookings before momentum cooled.",
      },
    ],
    results: [
      { value: "238+", label: "Meaningful patient conversations" },
      { value: "20%", label: "High-intent hot prospects" },
      { value: "4", label: "High-value conversions" },
    ],
  },
  {
    id: "cs-04",
    index: "04",
    client: "Pharma Company Awareness Drive",
    category: "Social Proof · Podcast · Awareness",
    headline: "30 Episodes | 6 Top Surgeons | 90 Reels",
    result: "9 Lac+",
    metric: "Impressions — maximum reach & viewership",
    body: "A high-authority educational podcast series with leading surgeons that demystified joint replacement and robotic surgery, reaching lakhs of residents.",
    tags: ["Social Proof", "Podcast", "Awareness"],
    image:
      "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    span: "lg:col-span-7",
    objective:
      "To eliminate widespread patient hesitation surrounding joint replacement and robotic surgeries by demystifying procedure precision, costs, recovery timelines, and long-term lifestyle impact through a high-authority educational campaign sponsored by a leading pharmaceutical company.",
    challenge:
      "Over 40% of eligible candidates avoid necessary joint replacement surgeries due to deeply ingrained misconceptions about post-operative quality of life and robotic technology. The core challenge was breaking through patient fear and medical skepticism with genuine clinical authority, clear information, and zero sales fluff.",
    strategy: [
      {
        title: "KOL & Regional Authority Strategy",
        body: "Onboarded 7 premier orthopedic surgeons across strategic zones in Bangalore to establish immediate regional trust and clinical credibility.",
      },
      {
        title: "5-Part Deep-Dive Podcast Series",
        body: "Produced an empathetic, highly informative video series addressing patient FAQs, surgical realities, robotic precision, and real-world recovery outcomes.",
      },
      {
        title: "Dual-Purpose Distribution",
        body: "Scaled targeted video distribution across digital channels while equipping surgeons with high-value FAQ assets for their own YouTube channels and clinical patient consultations.",
      },
    ],
    results: [
      { value: "8,00,000+", label: "Bangalore residents reached" },
      { value: "+40%", label: "Engagement rate" },
      { value: "Long-Term Asset", label: "Shortened consultation cycles & boosted surgical adoption" },
    ],
  },
];

export const APPROACH_HEADING = "Structured Method. Proven Results.";

export const CHAPTERS = [
  {
    num: "01",
    title: "System Readiness",
    body: "We build and polish your online infrastructure for maximum conversion efficiency.",
  },
  {
    num: "02",
    title: "The Pilot Run",
    body: "We run controlled tests to discover your precise acquisition numbers and cost benchmarks.",
  },
  {
    num: "03",
    title: "Capital Growth",
    body: "We scale proven winning campaigns and invest strictly where profitability is guaranteed.",
  },
];

export const CTA = {
  heading: "Ready to convert?",
  subtext:
    "Why fill out a form when you can just drop a text? Reach out on WhatsApp for a quick, pitch-free growth consult. Straight answers, zero hassle.",
  button: "Chat on WhatsApp",
};
